import { log } from '@/log'
import { OriginalApiErrorResponse } from '@/models/api/response/original-api-error-response'
import { ErrorType } from '@/models/error/api/error'
import _ from 'lodash'
import { Response } from 'superagent'

/**
 * エンドユーザー向けのエラーメッセージ
 */
export type DisplayErrorMessage = string
type ActionString = 'カートに追加' | 'ご指定の数量に変更'
export const defaultAddItemToCartErrorMessage: DisplayErrorMessage =
  'カートに追加できませんでした。少し時間を置いてから、もう一度お試しください。<br>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
export const defaultUpdateItemCountErrorMessage: DisplayErrorMessage =
  'ご指定の数量に変更できませんでした。少し時間を置いてから、もう一度お試しください。<br>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'

export function parseAddItemValidationError(response: Response): string {
  return parseValidationError(response, 'カートに追加', defaultAddItemToCartErrorMessage)
}

export function parseUpdateItemCountValidationError(response: Response): string {
  return parseValidationError(response, 'ご指定の数量に変更', defaultUpdateItemCountErrorMessage)
}

/**
 * このエラーメッセージは現状のAPIレスポンスにもとづく暫定的な対応で、追って以下の検討を進める
 * - 現状カート画面を見てもどの商品に問題があるのか分からないので、違反状態の表示と解消のためのアクションを提示できるよう改善
 * - カート追加時に、既存のカートに問題がある場合にカート追加を拒否するかどうか意思決定
 * - カート追加を拒否した場合のフィードバックを改善（具体的な商品、理由、解決方法など）
 *
 * @see https://github.com/Everforth/growthpalette-front/issues/8166
 * @see https://my-color.esa.io/posts/1260
 *
 * サーバー側でのエラーの組み立て方がアイテム追加と個数変更で同じで (src/Model/Cart/Cart.php の getCartConsistenceError)
 * エラーの内容・理由が同じためにエラーメッセージの組み立て方も共通化している。
 * @see https://github.com/Everforth/growthpalette-front/pull/8193#discussion_r1808173503 *
 */
function parseValidationError(
  response: Response,
  actionString: ActionString,
  defaultErrorMessage: DisplayErrorMessage
): string {
  const error = new OriginalApiErrorResponse(response)

  const messagesOnSingleItem = _.compact([
    (
      error.contains(ErrorType.NotInStoreError) ||
      error.contains(ErrorType.NotOnSaleError)
    )
      && `在庫切れのため、この商品を${actionString}できませんでした。`,
  ])
  if (messagesOnSingleItem.length > 0) {
    log.error({ messagesOnSingleItem })

    return messagesOnSingleItem.join('<br>')
  }

  const messagesOnWholeCart = _.compact([
    (
      error.contains(ErrorType.UnitCountExceededError) ||
      error.contains(ErrorType.MustWithinRootPurchaseLimit) ||
      error.contains(ErrorType.MustWithinSKUPurchaseLimit)
    )
      && '一度にご注文いただける商品数を超えている、もしくはカート内の商品が購入上限数に達しております。<br>カートをご確認いただき、商品の削除をお願いします。',
    error.contains(ErrorType.MustWithinLimitTimesOfPurchase)
      && 'ご購入回数に制限のある商品があるため、カートをご確認いただき、商品の削除をお願いします。',
    error.contains(ErrorType.MustWithoutMixedPrice)
      && 'ゲスト価格の商品とメンバー価格の商品は同時にご購入いただけません。<br>カートをご確認いただき、商品の削除をお願いします。',
    error.contains(ErrorType.MustWithinStock)
      && '在庫が不足しておりご購入できない商品がございます。<br>カートをご確認いただき、商品の削除をお願いします。',
  ])

  if (messagesOnWholeCart.length > 0) {
    log.error({ messagesOnWholeCart })

    return [
      `${actionString}できませんでした。`,
      ...messagesOnWholeCart,
    ].join('<br>')
  }

  // エラー自体はあるものの上記のパターンに該当しないものだけだった場合や、
  // そもそもエラーでないレスポンスをこの関数が入力として受け取った場合などに、デフォルトのエラーメッセージを返す。
  return defaultErrorMessage
}
